import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { fetchTableData, fetchProductionReport } from '../services/api';
import { useAuth } from './AuthContext';

const ProductionDataContext = createContext(null);

export const useProductionData = () => {
  const context = useContext(ProductionDataContext);
  if (!context) {
    throw new Error('useProductionData must be used within a ProductionDataProvider');
  }
  return context;
};

// Configuration for all production data tables
const DATA_CONFIG = {
  // Sampling Data
  sampling: {
    development: {
      tableName: 'tblLogUGDevelopmentSampling',
      key: 'developmentSampling',
    },
    stoping: {
      tableName: 'tblLogUGStopingSampling',
      key: 'stopingSampling',
    },
    tramming: {
      tableName: 'tblLogUGTrammingSampling',
      key: 'trammingSampling',
    },
    hoisting: {
      tableName: 'tblLogUGHoistingSampling',
      key: 'hoistingSampling',
    },
    trucking: {
      tableName: 'tblLogALLTruckingSamples',
      key: 'truckingSampling',
    },
  },
  // Production Data
  production: {
    development: {
      tableName: 'tblLogUGDevelopmentDrillBlast',
      key: 'developmentProduction',
    },
    stoping: {
      tableName: 'tblLogUGStopingDrillBlast',
      key: 'stopingProduction',
    },
    tramming: {
      tableName: 'tblLogUGTrammingActualTonnage',
      key: 'trammingProduction',
    },
    hoisting: {
      tableName: 'tblLogUGHoistingActualTonnage',
      key: 'hoistingProduction',
    },
    trucking: {
      tableName: 'tblLogALLTruckingActualTonnage',
      key: 'truckingProduction',
    },
  },
  // Grade Data
  grades: {
    pickstoneLab: {
      tableName: 'tblLogALLAssaysPickstoneLab',
      key: 'pickstoneLab',
    },
    chantonLab: {
      tableName: 'tblLogALLAssaysChantonLab',
      key: 'chantonLab',
    },
    batchDetails: {
      tableName: 'tblCatALLAssayBatchDetails',
      key: 'batchDetails',
    },
  },
};

const productionTypeMap = {
  developmentProduction: 'development',
  stopingProduction: 'stoping',
  trammingProduction: 'tramming',
  hoistingProduction: 'hoisting',
};

// Function to join grade data with sampling data
const joinGradeDataWithSampling = (samplingData, pickstoneLab, chantonLab, batchDetails) => {
  if (!samplingData || !Array.isArray(samplingData) || samplingData.length === 0) {
    return samplingData;
  }

  const pickstoneMap = new Map();
  const batchMap = new Map();
  const chantonMap = new Map();

  if (pickstoneLab && Array.isArray(pickstoneLab)) {
    pickstoneLab.forEach((item) => {
      if (item.samp_id) {
        if (!pickstoneMap.has(item.samp_id)) {
          pickstoneMap.set(item.samp_id, []);
        }
        pickstoneMap.get(item.samp_id).push(item);
      }
    });
  }

  if (batchDetails && Array.isArray(batchDetails)) {
    batchDetails.forEach((item) => {
      if (item.test_report_number) {
        batchMap.set(item.test_report_number, item);
      }
    });
  }

  if (chantonLab && Array.isArray(chantonLab)) {
    chantonLab.forEach((item) => {
      if (item.sample_id) {
        if (!chantonMap.has(item.sample_id)) {
          chantonMap.set(item.sample_id, []);
        }
        chantonMap.get(item.sample_id).push(item);
      }
    });
  }

  return samplingData.map((sample) => {
    const enriched = { ...sample };
    const sampId = sample.samp_id || sample.sample_id || sample.sample_id_brk || sample.sample_id_jv;

    if (!sampId) {
      return enriched;
    }

    const pickstoneData = pickstoneMap.get(sampId) || [];
    const bottleRollGrades = [];
    const otherPickstoneGrades = [];

    pickstoneData.forEach((pk) => {
      const batchDetail = pk.report_num ? batchMap.get(pk.report_num) : null;
      const testType = batchDetail?.test_type || batchDetail?.type || '';

      if (testType && testType.toUpperCase().includes('BOTTLE ROLL')) {
        bottleRollGrades.push(parseFloat(pk.samp_au_g_t) || 0);
      } else {
        otherPickstoneGrades.push(parseFloat(pk.samp_au_g_t) || 0);
      }
    });

    if (bottleRollGrades.length > 0) {
      enriched.grade_pk_br =
        bottleRollGrades.reduce((sum, grade) => sum + grade, 0) / bottleRollGrades.length;
    }

    if (otherPickstoneGrades.length > 0) {
      enriched.grade_pk_fa =
        otherPickstoneGrades.reduce((sum, grade) => sum + grade, 0) / otherPickstoneGrades.length;
    }

    const chantonData = chantonMap.get(sampId) || [];
    if (chantonData.length > 0) {
      const chantonGrades = chantonData.map((ch) => parseFloat(ch.result)).filter((grade) => !isNaN(grade));

      if (chantonGrades.length > 0) {
        enriched.grade_jv_br = chantonGrades.reduce((sum, grade) => sum + grade, 0) / chantonGrades.length;
      }
    }

    return enriched;
  });
};

const LOAD_CONCURRENCY = 2;

async function runPool(jobs, concurrency, onResult) {
  let cursor = 0;
  const worker = async () => {
    while (true) {
      const i = cursor;
      cursor += 1;
      if (i >= jobs.length) return;
      const result = await jobs[i]();
      onResult?.(result);
    }
  };
  const n = Math.min(Math.max(1, concurrency), jobs.length);
  await Promise.all(Array.from({ length: n }, () => worker()));
}

async function loadSamplingTable(config) {
  try {
    const result = await fetchTableData(config.tableName);
    if (result.success) {
      return { key: config.key, data: result.data || [] };
    }
    console.warn(`[ProductionData] Failed ${config.key}:`, result.error);
    return { key: config.key, data: [] };
  } catch (err) {
    console.warn(`[ProductionData] Error ${config.key}:`, err);
    return { key: config.key, data: [] };
  }
}

async function loadProductionEntry(config) {
  const reportType = productionTypeMap[config.key];
  try {
    if (reportType) {
      const result = await fetchProductionReport(reportType);
      if (result.success && result.data) {
        return { key: config.key, data: result.data };
      }
      console.warn(`[ProductionData] Failed ${config.key}:`, result.error);
      return { key: config.key, data: null };
    }
    const result = await fetchTableData(config.tableName);
    if (result.success) {
      return { key: config.key, data: result.data || [] };
    }
    console.warn(`[ProductionData] Failed ${config.key}:`, result.error);
    return { key: config.key, data: [] };
  } catch (err) {
    console.warn(`[ProductionData] Error ${config.key}:`, err);
    return { key: config.key, data: reportType ? null : [] };
  }
}

export const ProductionDataProvider = ({ children }) => {
  const { authenticated } = useAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Background load — does not use the scene/model LoadingTracker.
  const loadAllData = useCallback(async () => {
    if (!authenticated) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const jobs = [
        ...Object.values(DATA_CONFIG.sampling).map((config) => () => loadSamplingTable(config)),
        ...Object.values(DATA_CONFIG.production).map((config) => () => loadProductionEntry(config)),
        ...Object.values(DATA_CONFIG.grades).map((config) => () => loadSamplingTable(config)),
      ];

      await runPool(jobs, LOAD_CONCURRENCY, ({ key, data: payload }) => {
        setData((prev) => ({ ...prev, [key]: payload }));
      });

      setLastRefresh(new Date());
    } catch (err) {
      console.error('[ProductionData] Error loading production data:', err);
      setError(err.message || 'Failed to load production data');
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  // Start only after the GLB/hole catalogue has loaded — avoids competing with list_models.php.
  useEffect(() => {
    if (!authenticated) {
      setData({});
      setLastRefresh(null);
      return undefined;
    }

    const startBackgroundLoad = () => {
      window.setTimeout(() => loadAllData(), 250);
    };

    const onCatalogueReady = () => startBackgroundLoad();

    if (window.__mdvCatalogueReady) {
      startBackgroundLoad();
      return undefined;
    }

    window.addEventListener('mdv-catalogue-ready', onCatalogueReady);
    return () => window.removeEventListener('mdv-catalogue-ready', onCatalogueReady);
  }, [authenticated, loadAllData]);

  const refreshData = useCallback(() => {
    loadAllData();
  }, [loadAllData]);

  const getData = useCallback(
    (key) => data[key] || [],
    [data]
  );

  const getSamplingData = useCallback(
    (type) => {
      const key = DATA_CONFIG.sampling[type]?.key;
      if (!key) return [];

      const samplingData = getData(key);
      const pickstoneLab = getData('pickstoneLab');
      const chantonLab = getData('chantonLab');
      const batchDetails = getData('batchDetails');

      return joinGradeDataWithSampling(samplingData, pickstoneLab, chantonLab, batchDetails);
    },
    [getData]
  );

  const getDevelopmentSampling = useCallback(() => getSamplingData('development'), [getSamplingData]);
  const getStopingSampling = useCallback(() => getSamplingData('stoping'), [getSamplingData]);
  const getTrammingSampling = useCallback(() => getSamplingData('tramming'), [getSamplingData]);
  const getHoistingSampling = useCallback(() => getSamplingData('hoisting'), [getSamplingData]);
  const getTruckingSampling = useCallback(() => getSamplingData('trucking'), [getSamplingData]);

  const getDevelopmentProduction = useCallback(() => {
    const reportData = getData('developmentProduction');
    if (reportData && reportData.trend) {
      return reportData.trend.map((entry) => ({
        dev_end_id: entry.entity_id || null,
        advance_date: entry.date,
        est_tonnage_t: entry.actualTonnage || 0,
        planned_tonnage_t: entry.budgetTonnage || 0,
        actual_grade: entry.actualGrade || null,
        budget_grade: entry.budgetGrade || null,
        actual_content: entry.actualContent || 0,
        budget_content: entry.budgetContent || 0,
      }));
    }
    return [];
  }, [getData]);

  const getStopingProduction = useCallback(() => {
    const reportData = getData('stopingProduction');
    if (reportData && reportData.trend) {
      return reportData.trend.map((entry) => ({
        stope_id: entry.entity_id || null,
        drilling_date: entry.date,
        est_tonnage_t: entry.actualTonnage || 0,
        planned_tonnage_t: entry.budgetTonnage || 0,
        actual_grade: entry.actualGrade || null,
        budget_grade: entry.budgetGrade || null,
        actual_content: entry.actualContent || 0,
        budget_content: entry.budgetContent || 0,
      }));
    }
    return [];
  }, [getData]);

  const getTrammingProduction = useCallback(() => {
    const reportData = getData('trammingProduction');
    if (reportData && reportData.trend) {
      return reportData.trend.map((entry) => ({
        stope_id: entry.entity_id || null,
        tramming_date: entry.date,
        actual_tonnage_t: entry.actualTonnage || 0,
        budget_tonnage_t: entry.budgetTonnage || 0,
        actual_au_g_t: entry.actualGrade || null,
        budget_au_g_t: entry.budgetGrade || null,
        actual_content: entry.actualContent || 0,
        budget_content: entry.budgetContent || 0,
      }));
    }
    return [];
  }, [getData]);

  const getHoistingProduction = useCallback(() => {
    const reportData = getData('hoistingProduction');
    if (reportData && reportData.trend) {
      return reportData.trend.map((entry) => ({
        hoist_fac_name: entry.entity_id || null,
        hoisting_date: entry.date,
        hoisted_tonnage_t: entry.actualTonnage || 0,
        budget_tonnage_t: entry.budgetTonnage || 0,
        actual_grade: entry.actualGrade || null,
        budget_grade: entry.budgetGrade || null,
        actual_content: entry.actualContent || 0,
        budget_content: entry.budgetContent || 0,
      }));
    }
    return [];
  }, [getData]);

  const getTruckingProduction = useCallback(() => getData('truckingProduction'), [getData]);

  const getProductionReport = useCallback(
    (type) => {
      const keyMap = {
        development: 'developmentProduction',
        stoping: 'stopingProduction',
        tramming: 'trammingProduction',
        hoisting: 'hoistingProduction',
      };
      const key = keyMap[type];
      return key ? getData(key) : null;
    },
    [getData]
  );

  const getDevelopmentDrilling = useCallback(() => getData('developmentProduction'), [getData]);
  const getProductionDrilling = useCallback(() => getData('stopingProduction'), [getData]);

  const value = {
    data,
    loading,
    error,
    lastRefresh,
    loadAllData,
    refreshData,
    getData,
    getSamplingData,
    getDevelopmentSampling,
    getStopingSampling,
    getTrammingSampling,
    getHoistingSampling,
    getTruckingSampling,
    getDevelopmentProduction,
    getStopingProduction,
    getTrammingProduction,
    getHoistingProduction,
    getTruckingProduction,
    getProductionReport,
    getDevelopmentDrilling,
    getProductionDrilling,
    config: DATA_CONFIG,
  };

  return <ProductionDataContext.Provider value={value}>{children}</ProductionDataContext.Provider>;
};
